import { useEffect, useState } from 'react'
import { cdApi } from '../../api/changeDetection'
import type { CdProject } from '../../types/changeDetection'

interface Props {
  open: boolean
  project?: CdProject | null
  onClose: () => void
  onSaved: (project: CdProject) => void
}

export default function CdNewProjectModal({ open, project, onClose, onSaved }: Props) {
  const editing = !!project
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(project?.name ?? '')
    setDescription(project?.description ?? '')
    setError('')
  }, [open, project])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!name.trim()) {
      setError('프로젝트 이름을 입력해주세요.')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        centerLon: project?.centerLon ?? null,
        centerLat: project?.centerLat ?? null,
      }
      const saved = editing
        ? await cdApi.updateProject(project!.id, payload)
        : await cdApi.createProject(payload)
      onSaved(saved)
      onClose()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '저장에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden text-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-base font-bold text-white">{editing ? '프로젝트 수정' : '새 프로젝트'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">프로젝트 정보</p>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">이름</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 둔포면 — Waste Site"
              autoFocus
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">설명 <span className="text-slate-500">(선택)</span></label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="간단한 설명"
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800">
              취소
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white"
            >
              {submitting ? '저장 중...' : editing ? '저장' : '프로젝트 생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
